import React, { useState, FormEvent, useEffect } from 'react';
import { getDepartments } from '../../services/migration/departmentDataService';
import { styled } from '@mui/material/styles';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { authApi } from '../../config/apiClient';
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  Autocomplete
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import CorporateFareIcon from '@mui/icons-material/CorporateFare'; // Departman ikonu
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// Stil tanımlamaları (PersonnelRegister.tsx'ten uyarlandı)
const ModalContent = styled(Paper)(({ theme }) => ({
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 380, // Genişlik daraltıldı
  maxHeight: '90vh',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  padding: theme.spacing(2, 4, 3), // Padding ayarlandı
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));


const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius: 12,
  fontWeight: 'bold',
  fontSize: 16,
  width: '100%',
  backgroundColor: '#0D47A1',
  '&:hover': {
    backgroundColor: '#0A3C87',
  },
}));

interface CreatePersonnelModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null; // Adminin companyId'si
  companyName?: string | null; // Adminin companyName'i (opsiyonel, API isteğine eklenebilir)
  onPersonnelCreated?: () => void; // Personel oluşturulduktan sonra çağrılacak callback
}

const CreatePersonnelModal: React.FC<CreatePersonnelModalProps> = ({
  open,
  onClose,
  companyId,
  companyName,
  onPersonnelCreated
}) => {
  const { t, i18n } = useTranslation(['humanResources', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  // AuthContext'ten branches al
  const { branches } = useAuth();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  // Şube state'leri - branches artık AuthContext'ten geliyor
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

  // Departman state'leri
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Modal kapandığında form state'lerini sıfırla
  useEffect(() => {
    if (!open) {
      setFirstName('');
      setLastName('');
      setPhone('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setLoading(false);
      setSelectedBranchId(null); // Şube seçimini de sıfırla
      // branches artık AuthContext'ten geliyor, sıfırlamaya gerek yok
      setSelectedDepartmentId(null); // Departman seçimini sıfırla
      setDepartments([]); // Departman listesini sıfırla
    } else {
      if (companyId) {
        // branches artık AuthContext'ten geliyor, yükleme gerek yok
        loadDepartments(companyId); // Departmanları yükle
      }
    }
  }, [open, companyId]);

  // Şubeler artık AuthContext'ten geliyor, ayrı yükleme fonksiyonu gerekmiyor

  // Departmanları SQL'den yükleme fonksiyonu (GET /api/hr/departments)
  const loadDepartments = async (currentCompanyId: string) => {
    setLoadingDepartments(true);
    try {
      const list = await getDepartments(currentCompanyId);
      const departmentsList = (list || []).map((d) => ({
        id: d.id,
        name: d.name || hr('createPersonnel.departmentUnnamed', 'Unnamed Department'),
      }));
      setDepartments(departmentsList);
    } catch (err) {
      console.error("Departmanlar yüklenirken hata:", err);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Telefon numarasını E.164 formatına dönüştüren yardımcı fonksiyon
  const formatPhoneToE164 = (phoneNumber: string): string | null => {
    // Boş veya tanımsız telefon
    if (!phoneNumber || phoneNumber.trim() === '') {
      return null;
    }

    // Tüm rakam olmayan karakterleri kaldır
    let digits = phoneNumber.replace(/\D/g, '');
    
    // Türkiye numarası kontrolü
    if (digits.startsWith('0')) {
      digits = digits.substring(1); // Baştaki 0'ı kaldır
    }
    
    // Eğer 10 hane değilse (Türkiye için standart), geçersiz kabul et
    if (digits.length !== 10) {
      console.log('Geçersiz telefon numarası formatı:', phoneNumber);
      return null;
    }
    
    // E.164 formatında dönüştür (+90...)
    return `+90${digits}`;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!firstName) {
      setError(hr('createPersonnel.validation.firstNameRequired', 'Please enter first name'));
      return;
    }
    if (!lastName) {
      setError(hr('createPersonnel.validation.lastNameRequired', 'Please enter last name'));
      return;
    }
    if (!email) {
      setError(hr('createPersonnel.validation.emailRequired', 'Please enter email address'));
      return;
    }
    if (!email.includes('@')) {
      setError(hr('createPersonnel.validation.emailInvalid', 'Please enter a valid email address'));
      return;
    }
    if (!password) {
      setError(hr('createPersonnel.validation.passwordRequired', 'Please enter password'));
      return;
    }
    if (password.length < 6) {
      setError(hr('createPersonnel.validation.passwordMinLength', 'Password must be at least 6 characters'));
      return;
    }
    if (password !== confirmPassword) {
      setError(hr('createPersonnel.validation.passwordMismatch', 'Passwords do not match'));
      return;
    }
    if (!companyId) {
      setError(hr('createPersonnel.validation.companyNotLoaded', 'Company information could not be loaded. Please try again.'));
      return;
    }
    
    const adminUid = ownAuthSession.getCurrentUid(); // own-auth oturumundan admin kullanıcısını al
    if (!adminUid) {
        setError(hr('createPersonnel.validation.adminSessionMissing', 'Admin session not found. Please sign in again.'));
        return;
    }

    setLoading(true);
    setError('');
    setErrorDetails(null);
    setSuccess('');
    setTransactionId(null);
    
    try {
      const formattedPhone = formatPhoneToE164(phone);
      console.log('Formatlanmış telefon numarası:', formattedPhone);

      const personnelDataApi = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password: password,
        phone: formattedPhone,
        role: 'employee',
        branchesId: selectedBranchId,
        departmentId: selectedDepartmentId
      };

      console.log('API isteğine gönderilecek veriler:', personnelDataApi);

      const response = await authApi.post('/api/auth/create-personnel-by-admin', personnelDataApi);

      const result = response.data;
      console.log('API yanıt:', result);

      if (result.transactionId) {
        setTransactionId(result.transactionId);
        console.log('Transaction ID:', result.transactionId);
      }
      
      setSuccess(result.message || hr('createPersonnel.messages.created', 'Personnel was created and added to the company successfully.'));
      if (onPersonnelCreated) {
        onPersonnelCreated(); // Personel listesini yenilemek için callback
      }
      setTimeout(() => {
        onClose(); // Başarılı olursa modalı kapat
      }, 2500);
      
    } catch (err: any) {
      const responseData = err.response?.data;
      let errorMessage = responseData?.message || err.message || 'Personel oluşturulamadı.';
      
      if (responseData?.transactionId) {
        setTransactionId(responseData.transactionId);
      }
      
      if (responseData?.error) {
        setErrorDetails(typeof responseData.error === 'string' ? responseData.error : JSON.stringify(responseData.error));
        console.error('Hata detayları:', responseData.error);
      }
      
      if (err.code) {
        switch (err.code) {
          case 'auth/email-already-exists':
            errorMessage = hr('createPersonnel.errors.emailInUse', 'This email address is already used by another personnel.');
            break;
          case 'auth/invalid-email':
            errorMessage = hr('createPersonnel.errors.invalidEmail', 'Invalid email address.');
            break;
          case 'auth/weak-password':
            errorMessage = hr('createPersonnel.errors.weakPassword', 'Password is not strong enough.');
            break;
          default:
            break;
        }
      }
      
      setError(errorMessage);
      
      if (transactionId) {
        setErrorDetails(`İşlem takip numarası: ${transactionId}. Bu numara ile sistem yöneticisine başvurabilirsiniz.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = firstName && lastName && email && password && confirmPassword && password === confirmPassword;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-personnel-modal-title"
      aria-describedby="create-personnel-modal-description"
    >
      <ModalContent>
        <ModalHeader>
          <Typography variant="h5" component="h1" fontWeight="bold" id="create-personnel-modal-title">
            {hr('createPersonnel.title', 'Create Personnel')}
          </Typography>
          <IconButton onClick={onClose} aria-label={hr('common.closeLower', 'close')}>
            <CloseIcon />
          </IconButton>
        </ModalHeader>
        
        <Typography 
          variant="body2" 
          sx={{ mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}
          id="create-personnel-modal-description"
        >
        
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} width="100%" sx={{ mt: 1 }}>
          {/* Ad */}
          <StyledTextField
            variant="outlined"
            fullWidth
            id="firstName"
            label={hr('createPersonnel.fields.firstName', 'First Name')}
            name="firstName"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Soyad */}
          <StyledTextField
            variant="outlined"
            fullWidth
            id="lastName"
            label={hr('createPersonnel.fields.lastName', 'Last Name')}
            name="lastName"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Telefon (Opsiyonel) */}
          <StyledTextField
            variant="outlined"
            fullWidth
            id="phone"
            label={hr('createPersonnel.fields.phoneOptional', 'Phone (Optional)')}
            name="phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          {/* E-posta */}
          <StyledTextField
            variant="outlined"
            fullWidth
            id="email"
            label={hr('createPersonnel.fields.email', 'Email')}
            name="email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Şifre */}
          <StyledTextField
            variant="outlined"
            fullWidth
            name="password"
            label={hr('createPersonnel.fields.password', 'Password')}
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Şifre Tekrar */}
          <StyledTextField
            variant="outlined"
            fullWidth
            name="confirmPassword"
            label={hr('createPersonnel.fields.confirmPassword', 'Confirm Password')}
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Departman Seçimi (Opsiyonel) */}
          <Autocomplete
            options={departments}
            getOptionLabel={(option) => option.name}
            value={departments.find(dept => dept.id === selectedDepartmentId) || null}
            onChange={(event, newValue) => {
              setSelectedDepartmentId(newValue ? newValue.id : null);
            }}
            loading={loadingDepartments}
            freeSolo={false} // Departman listesinden SADECE seçim yapılabilir, manuel giriş yapılamaz
            disableClearable={false} // Seçimi temizleme özelliğini açık bırakıyoruz
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label={hr('createPersonnel.fields.departmentOptional', 'Department (Optional)')}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CorporateFareIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {loadingDepartments ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                 <CorporateFareIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                  {option.name}
                </li>
            )}
            sx={{ marginBottom: 1.5 }}
          />

          {/* Şube Seçimi (Opsiyonel) */}
          <Autocomplete
            options={branches}
            getOptionLabel={(option) => option.name}
            value={branches.find(branch => branch.id === selectedBranchId) || null}
            onChange={(event, newValue) => {
              setSelectedBranchId(newValue ? newValue.id : null);
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label={hr('createPersonnel.fields.branchOptional', 'Branch (Optional)')}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                 <BusinessIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                  {option.name}
                </li>
            )}
            sx={{ marginBottom: 1.5 }}
          />

          <SubmitButton
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !isFormValid}
            size="medium"
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : hr('createPersonnel.submit', 'Create Personnel and Add to Company')}
          </SubmitButton>
        </Box>
        
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setError('')} severity="error" variant="filled">
            {error}
          </Alert>
        </Snackbar>
        
        {/* Hata detayları varsa göster */}
        {errorDetails && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Alert 
              severity="warning" 
              variant="outlined"
              onClose={() => setErrorDetails(null)}
              sx={{ fontSize: '0.8rem' }}
            >
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                {hr('createPersonnel.errorDetails', 'Error Details:')}
              </Typography>
              <Typography variant="caption" component="pre" sx={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                mt: 0.5
              }}>
                {errorDetails}
              </Typography>
              {transactionId && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
                  {hr('createPersonnel.transactionId', 'Transaction ID')}: {transactionId}
                </Typography>
              )}
            </Alert>
          </Box>
        )}
        
        <Snackbar 
          open={!!success} 
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" variant="filled">
            {success}
          </Alert>
        </Snackbar>
      </ModalContent>
    </Modal>
  );
};

export default CreatePersonnelModal; 